/**
 * Iter 67be — Fleet finance tab (vendor dashboard).
 *
 * Two sections:
 *   1. Year-grouped P&L summary table (cars × months × {revenue, expenses, net})
 *   2. Expense ledger with add / edit / delete + CSV export + per-vehicle PDF
 *
 * Revenue is the vendor's NET after platform commission (rental_total − commission)
 * for confirmed/completed bookings, so the on-screen P&L mirrors what the vendor
 * actually keeps.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, FileSpreadsheet, FileText, Trash2, Pencil, TrendingUp, TrendingDown, Globe } from "lucide-react";
import Money from "@/components/Money";
import { useCurrency } from "@/lib/currency";
import RecurringExpensesPanel from "@/components/vendor/RecurringExpensesPanel";
import SnoozedCarsPanel from "@/components/vendor/SnoozedCarsPanel";

// Mirrors VEHICLE_EXPENSE_CATEGORIES on the backend. The amber chip colour
// across the table comes from this list — adding a category here is
// effectively free (the backend Literal will reject anything not listed).
const CATEGORIES = [
  { value: "maintenance",      label: "Maintenance" },
  { value: "tires",            label: "Tires" },
  { value: "battery",          label: "Battery" },
  { value: "insurance",        label: "Insurance" },
  { value: "registration_tax", label: "Registration / Road tax" },
  { value: "repair",           label: "Repair" },
  { value: "inspection",       label: "Inspection (ITP/MOT)" },
  { value: "cleaning",         label: "Cleaning" },
  { value: "fuel",             label: "Fuel" },
  { value: "parking",          label: "Parking" },
  { value: "fine",             label: "Fine" },
  { value: "other",            label: "Other" },
];

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function _categoryLabel(value) {
  return CATEGORIES.find((c) => c.value === value)?.label || value;
}

function _newExpenseDraft(carId = "") {
  return {
    car_id: carId,
    category: "maintenance",
    label: "",
    // Iter 67bn — `amount` is in the chosen currency; backend converts to EUR.
    amount: "",
    currency: "EUR",
    expense_date: new Date().toISOString().slice(0, 10),
    vat_amount_eur: "",
    notes: "",
  };
}

function _net(amount) {
  return Number(amount || 0);
}

export default function FleetFinanceTab() {
  const currentYear = new Date().getFullYear();
  const [searchParams] = useSearchParams();
  const [year, setYear] = useState(currentYear);
  // Iter 67bg — `?car=<id>` deep-link from FleetHealthTile pre-filters.
  const [carFilter, setCarFilter] = useState(searchParams.get("car") || "all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [pnl, setPnl] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);    // null = create, object = edit
  const [draft, setDraft] = useState(_newExpenseDraft());
  const [saving, setSaving] = useState(false);
  // Iter 67bn — list of supported currencies + cached rates from /fx/rates.
  // Used to populate the dialog's currency picker and preview the converted
  // EUR amount in real time as the vendor types.
  const { supported: supportedCurrencies = ["EUR"], rates: fxRates = {} } = useCurrency();

  const load = async () => {
    setLoading(true);
    try {
      const pnlParams = { year };
      if (carFilter !== "all") pnlParams.car_id = carFilter;
      const expParams = {
        from: `${year}-01-01`,
        to: `${year + 1}-01-01`,
      };
      if (carFilter !== "all") expParams.car_id = carFilter;
      if (categoryFilter !== "all") expParams.category = categoryFilter;

      const [pnlR, expR] = await Promise.all([
        api.get("/vendor/fleet-pnl", { params: pnlParams }),
        api.get("/vendor/vehicle-expenses", { params: expParams }),
      ]);
      setPnl(pnlR.data);
      setExpenses(expR.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not load fleet finance data.");
    } finally {
      setLoading(false);
    }
  };

  // Reload when filters change. eslint-disable: load is stable enough.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { load(); }, [year, carFilter, categoryFilter]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const cars = pnl?.cars || [];
  const totals = pnl?.totals || { revenue: 0, expenses: 0, net: 0, by_car: [] };

  const yearOptions = useMemo(() => {
    const arr = [];
    for (let y = currentYear + 1; y >= currentYear - 4; y--) arr.push(y);
    return arr;
  }, [currentYear]);

  const expensesTotal = useMemo(
    () => expenses.reduce((s, e) => s + Number(e.amount_eur || 0), 0),
    [expenses],
  );

  const onOpenCreate = () => {
    setEditing(null);
    setDraft(_newExpenseDraft(carFilter !== "all" ? carFilter : (cars[0]?.id || "")));
    setDialogOpen(true);
  };
  const onOpenEdit = (e) => {
    setEditing(e);
    setDraft({
      car_id: e.car_id,
      category: e.category,
      label: e.label || "",
      // Iter 67bn — preserve native amount/currency when editing. Falls back to
      // the converted EUR figure for legacy rows that pre-date multi-currency.
      amount: String(e.amount ?? e.amount_eur ?? ""),
      currency: e.currency || "EUR",
      expense_date: (e.expense_date || "").slice(0, 10),
      vat_amount_eur: e.vat_amount_eur != null ? String(e.vat_amount_eur) : "",
      notes: e.notes || "",
    });
    setDialogOpen(true);
  };
  const onSave = async () => {
    if (!draft.car_id) { toast.error("Pick a vehicle first."); return; }
    if (!draft.amount || Number(draft.amount) <= 0) { toast.error("Amount must be > 0."); return; }
    setSaving(true);
    try {
      const body = {
        car_id: draft.car_id,
        category: draft.category,
        label: draft.label || null,
        amount: Number(draft.amount),
        currency: draft.currency || "EUR",
        expense_date: draft.expense_date,
        vat_amount_eur: draft.vat_amount_eur ? Number(draft.vat_amount_eur) : null,
        notes: draft.notes || null,
      };
      if (editing) {
        // PATCH doesn't accept car_id changes — only the editable fields.
        const patchBody = { ...body };
        delete patchBody.car_id;
        await api.patch(`/vendor/vehicle-expenses/${editing.id}`, patchBody);
        toast.success("Expense updated.");
      } else {
        await api.post("/vendor/vehicle-expenses", body);
        toast.success("Expense added.");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };
  const onDelete = async (e) => {
    if (!window.confirm(`Delete this ${_categoryLabel(e.category)} expense (€${Number(e.amount_eur).toFixed(2)})?`)) return;
    try {
      await api.delete(`/vendor/vehicle-expenses/${e.id}`);
      toast.success("Expense deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Delete failed.");
    }
  };

  const onCsvExport = () => {
    const qs = new URLSearchParams();
    qs.set("from", `${year}-01-01`);
    qs.set("to", `${year + 1}-01-01`);
    if (carFilter !== "all") qs.set("car_id", carFilter);
    if (categoryFilter !== "all") qs.set("category", categoryFilter);
    window.open(`${API}/vendor/vehicle-expenses.csv?${qs.toString()}`, "_blank", "noopener,noreferrer");
  };

  const onPdfReport = (carId) => {
    window.open(`${API}/vendor/vehicle-expenses/${carId}/report.pdf?year=${year}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-8" data-testid="fleet-finance-tab">
      {/* Filters + summary cards */}
      <div className="flex flex-wrap items-end gap-3" data-testid="fleet-finance-filters">
        <div>
          <Label className="text-xs">Year</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="mt-1 w-28" data-testid="fleet-finance-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Vehicle</Label>
          <Select value={carFilter} onValueChange={setCarFilter}>
            <SelectTrigger className="mt-1 w-60" data-testid="fleet-finance-car"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All vehicles</SelectItem>
              {cars.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}{c.plate ? ` · ${c.plate}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Category</Label>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="mt-1 w-48" data-testid="fleet-finance-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={onCsvExport} className="rounded-full" data-testid="fleet-finance-csv">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> CSV
          </Button>
          <Button onClick={onOpenCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="fleet-finance-add">
            <Plus className="h-4 w-4 mr-1.5" /> Add expense
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="fleet-finance-summary">
        <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl border border-emerald-100 p-5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-900">Revenue {year}</div>
          <div className="font-display text-2xl font-semibold text-emerald-900 mt-1 flex items-center gap-2">
            <TrendingUp className="h-5 w-5" /> <Money amount={totals.revenue} />
          </div>
          <div className="text-xs text-emerald-700/80 mt-1">Net of platform commission</div>
        </div>
        <div className="bg-gradient-to-br from-rose-50 to-white rounded-2xl border border-rose-100 p-5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-rose-900">Expenses {year}</div>
          <div className="font-display text-2xl font-semibold text-rose-900 mt-1 flex items-center gap-2">
            <TrendingDown className="h-5 w-5" /> <Money amount={totals.expenses} />
          </div>
          <div className="text-xs text-rose-700/80 mt-1">{expenses.length} entries</div>
        </div>
        <div className={`bg-gradient-to-br ${totals.net >= 0 ? "from-indigo-50" : "from-amber-50"} to-white rounded-2xl border ${totals.net >= 0 ? "border-indigo-100" : "border-amber-200"} p-5`}>
          <div className={`text-[10px] uppercase tracking-wider font-bold ${totals.net >= 0 ? "text-indigo-900" : "text-amber-900"}`}>Net {year}</div>
          <div className={`font-display text-2xl font-semibold mt-1 ${totals.net >= 0 ? "text-indigo-900" : "text-amber-900"}`}>
            <Money amount={totals.net} />
          </div>
          <div className={`text-xs mt-1 ${totals.net >= 0 ? "text-indigo-700/80" : "text-amber-700/80"}`}>Revenue − Expenses</div>
        </div>
      </div>

      {/* P&L matrix — cars × months */}
      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="font-semibold text-neutral-900 text-sm">P&L by vehicle · {year}</div>
            <div className="text-xs text-neutral-500 mt-0.5">Monthly net = booking revenue − expenses</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-12 text-center text-neutral-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
          ) : cars.length === 0 ? (
            <div className="px-5 py-12 text-center text-neutral-500">No vehicles in your fleet yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="fleet-finance-pnl-table">
              <thead className="bg-neutral-50 border-b border-border text-[10px] uppercase tracking-wider text-neutral-600">
                <tr>
                  <th className="text-left px-4 py-3 sticky left-0 bg-neutral-50 z-10">Vehicle</th>
                  {MONTHS.map((m) => <th key={m} className="text-right px-2 py-3 min-w-[72px]">{m}</th>)}
                  <th className="text-right px-4 py-3 bg-neutral-100 font-bold">Total</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {cars.map((c) => {
                  const carTotal = totals.by_car?.find((b) => b.car_id === c.id) || { revenue: 0, expenses: 0, net: 0 };
                  return (
                    <tr key={c.id} className="border-b border-border last:border-0" data-testid={`fleet-finance-row-${c.id}`}>
                      <td className="px-4 py-3 sticky left-0 bg-white z-10">
                        <div className="font-semibold text-neutral-900">{c.label}</div>
                        {c.plate && <div className="text-xs text-neutral-500">{c.plate}</div>}
                        <div className="text-[11px] text-emerald-700 mt-1"><Money amount={carTotal.revenue} /> rev · <span className="text-rose-700"><Money amount={carTotal.expenses} /> exp</span></div>
                      </td>
                      {pnl.months.map((m) => {
                        const row = m.by_car.find((r) => r.car_id === c.id);
                        const net = row ? row.net : 0;
                        const tone = !row ? "text-neutral-300" : net > 0 ? "text-emerald-700" : net < 0 ? "text-rose-700" : "text-neutral-500";
                        return (
                          <td key={m.month} className={`text-right px-2 py-3 font-mono text-xs tabular-nums ${tone}`} title={row ? `Rev €${row.revenue.toFixed(2)} · Exp €${row.expenses.toFixed(2)}` : ""}>
                            {row ? <Money amount={net} /> : "—"}
                          </td>
                        );
                      })}
                      <td className={`text-right px-4 py-3 bg-neutral-50/60 font-semibold tabular-nums ${_net(carTotal.net) >= 0 ? "text-emerald-800" : "text-rose-800"}`} data-testid={`fleet-finance-total-${c.id}`}>
                        <Money amount={carTotal.net} />
                      </td>
                      <td className="text-right px-2">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onPdfReport(c.id)} title="Download per-vehicle PDF report" data-testid={`fleet-finance-pdf-${c.id}`}>
                          <FileText className="h-3.5 w-3.5 text-indigo-600" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-neutral-50 border-t border-border">
                <tr className="font-semibold text-sm">
                  <td className="px-4 py-3 sticky left-0 bg-neutral-50">Monthly total</td>
                  {pnl.months.map((m) => (
                    <td key={m.month} className={`text-right px-2 py-3 font-mono text-xs tabular-nums ${m.totals.net > 0 ? "text-emerald-800" : m.totals.net < 0 ? "text-rose-800" : "text-neutral-500"}`}>
                      <Money amount={m.totals.net} />
                    </td>
                  ))}
                  <td className={`text-right px-4 py-3 bg-neutral-100 ${totals.net >= 0 ? "text-emerald-900" : "text-rose-900"}`}><Money amount={totals.net} /></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      {/* Expense ledger */}
      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="font-semibold text-neutral-900 text-sm">Expense ledger · {year}</div>
            <div className="text-xs text-neutral-500 mt-0.5">{expenses.length} entries · <Money amount={expensesTotal} /></div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="fleet-finance-expenses-table">
            <thead className="bg-neutral-50 border-b border-border text-[10px] uppercase tracking-wider text-neutral-600">
              <tr>
                <th className="text-left px-4 py-3 w-28">Date</th>
                <th className="text-left px-4 py-3">Vehicle</th>
                <th className="text-left px-4 py-3 w-40">Category</th>
                <th className="text-left px-4 py-3">Description</th>
                <th className="text-right px-4 py-3 w-32">Amount</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</td></tr>}
              {!loading && expenses.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-500">No expenses recorded for this period.</td></tr>
              )}
              {!loading && expenses.map((e) => (
                <tr key={e.id} className="border-b border-border last:border-0 hover:bg-neutral-50/60" data-testid={`fleet-finance-expense-${e.id}`}>
                  <td className="px-4 py-3 text-sm tabular-nums">{e.expense_date}</td>
                  <td className="px-4 py-3 text-sm">{e.car_label || "—"}{e.car_plate ? <span className="text-neutral-500"> · {e.car_plate}</span> : null}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 uppercase tracking-wider text-[10px] font-bold">{_categoryLabel(e.category)}</Badge>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {e.label || <span className="text-neutral-400 italic">—</span>}
                    {e.notes && <div className="text-xs text-neutral-500 mt-0.5 truncate max-w-md" title={e.notes}>{e.notes}</div>}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">
                    <Money amount={e.amount_eur} />
                    {e.currency && e.currency !== "EUR" && e.amount != null && (
                      <div className="text-[10px] font-medium text-neutral-500 tabular-nums" data-testid={`fleet-finance-native-${e.id}`}>
                        {Number(e.amount).toLocaleString()} {e.currency}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-3 text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-neutral-600" onClick={() => onOpenEdit(e)} data-testid={`fleet-finance-edit-${e.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => onDelete(e)} data-testid={`fleet-finance-delete-${e.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Iter 67bv — Recurring expense rules + alert threshold settings */}
      <RecurringExpensesPanel cars={cars} onChanged={load} />

      {/* Iter 67ce — Snoozed cars panel (hidden when nothing snoozed) */}
      <SnoozedCarsPanel onChanged={load} />

      {/* Add / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !saving && setDialogOpen(o)}>
        <DialogContent className="sm:max-w-lg" data-testid="fleet-finance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editing ? <Pencil className="h-5 w-5 text-indigo-600" /> : <Plus className="h-5 w-5 text-indigo-600" />}
              {editing ? "Edit expense" : "Add expense"}
            </DialogTitle>
            <DialogDescription>Recorded against a specific vehicle for P&L reporting.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Vehicle</Label>
                <Select value={draft.car_id} onValueChange={(v) => setDraft({ ...draft, car_id: v })} disabled={!!editing}>
                  <SelectTrigger className="mt-1" data-testid="fleet-finance-dialog-car"><SelectValue placeholder="Pick a vehicle…" /></SelectTrigger>
                  <SelectContent>
                    {cars.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}{c.plate ? ` · ${c.plate}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="fleet-finance-dialog-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={draft.expense_date}
                  onChange={(e) => setDraft({ ...draft, expense_date: e.target.value })}
                  className="mt-1"
                  data-testid="fleet-finance-dialog-date"
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Input
                  value={draft.label}
                  onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                  placeholder="e.g. 4× Michelin winter tires"
                  className="mt-1"
                  data-testid="fleet-finance-dialog-label"
                />
              </div>
              <div>
                <Label>Amount</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="number" step="0.01" min="0"
                    value={draft.amount}
                    onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                    className="flex-1 tabular-nums"
                    data-testid="fleet-finance-dialog-amount"
                  />
                  <Select
                    value={draft.currency}
                    onValueChange={(v) => setDraft({ ...draft, currency: v })}
                  >
                    <SelectTrigger className="w-28" data-testid="fleet-finance-dialog-currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {supportedCurrencies.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {draft.currency && draft.currency !== "EUR" && Number(draft.amount) > 0 && (
                  <div className="text-[11px] text-neutral-500 mt-1 flex items-center gap-1" data-testid="fleet-finance-dialog-eur-preview">
                    <Globe className="h-3 w-3" />
                    ≈ €{(Number(draft.amount) / (Number(fxRates?.[draft.currency]) || 1)).toFixed(2)} <span className="text-neutral-400">· stored in EUR for reports</span>
                  </div>
                )}
              </div>
              <div>
                <Label>VAT (EUR, optional)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={draft.vat_amount_eur}
                  onChange={(e) => setDraft({ ...draft, vat_amount_eur: e.target.value })}
                  className="mt-1"
                  data-testid="fleet-finance-dialog-vat"
                />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  placeholder="Mileage, provider, warranty, anything you'd want to find later."
                  rows={2}
                  className="mt-1"
                  data-testid="fleet-finance-dialog-notes"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={onSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="fleet-finance-dialog-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : (editing ? <Pencil className="h-4 w-4 mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />)}
              {editing ? "Save changes" : "Add expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
